import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useSignupModal } from '../contexts/SignupModalContext';
import { translations } from '../translations';
import LanguageSwitcher from '../components/LanguageSwitcher';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { language } = useLanguage();
  const { openSignupModal } = useSignupModal();
  const t = translations[language];

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Function to handle smooth scrolling to sections on the same page
  const scrollToSection = (sectionId, event) => {
    // If we're already on the home page, handle smooth scrolling
    if (location.pathname === '/' || location.pathname === '') {
      event.preventDefault();
      const element = document.getElementById(sectionId);
      if (element) {
        // Get the header height to account for the sticky header
        const header = document.querySelector('header');
        const headerHeight = header ? header.offsetHeight : 0;
        
        // Calculate the target scroll position
        const elementPosition = element.offsetTop;
        const targetPosition = elementPosition - headerHeight;
        
        // Smooth scroll to the target position
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
        
        // Close mobile menu if it's open
        if (mobileMenuOpen) {
          setMobileMenuOpen(false);
        }
      }
    } else {
      // If we're on another page, navigate to home page with hash
      // The HomePage component will handle the scroll after navigation
      event.preventDefault();
      // Close mobile menu if it's open
      if (mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
      // Navigate to the home page with the hash
      window.location.href = `/#${sectionId}`;
    }
  };

  // Function to handle signup button clicks
  const handleSignupClick = (e) => {
    e.preventDefault();
    openSignupModal();
    // Close mobile menu if it's open
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center">
            <img src="/images/protego_w_text.png" alt="Sanctions Screener Logo" className="h-12" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:block">
            <ul className="hidden md:flex space-x-8">
              <li><Link to="/#home" className="text-gray-700 hover:text-blue-600 font-medium" onClick={(e) => scrollToSection('home', e)}>{t.home}</Link></li>
              <li><Link to="/#features" className="text-gray-700 hover:text-blue-600 font-medium" onClick={(e) => scrollToSection('features', e)}>{t.features}</Link></li>
              <li><Link to="/#pricing" className="text-gray-700 hover:text-blue-600 font-medium" onClick={(e) => scrollToSection('pricing', e)}>{t.pricing}</Link></li>
              <li><Link to="/about" className="text-gray-700 hover:text-blue-600 font-medium">{t.about}</Link></li>
              <li><Link to="/contact" className="text-gray-700 hover:text-blue-600 font-medium">{t.contact}</Link></li>
            </ul>
          </nav>

          {/* CTA Buttons and Language Switcher */}
          <div className="hidden md:flex items-center space-x-4">
            <LanguageSwitcher />
            <a href="https://app.sanctionscreener.eu" className="text-gray-700 hover:text-blue-600 font-medium">{t.login}</a>
            <button 
              onClick={handleSignupClick}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200"
            >
              {t.signup}
            </button>
          </div>

          {/* Mobile menu button */}
          <button 
            className="md:hidden text-gray-700"
            onClick={toggleMobileMenu}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="absolute left-0 right-0 bg-white shadow-lg md:hidden flex flex-col p-6 mt-2 z-50">
            <ul className="flex flex-col space-y-4 mt-4">
              <li><Link to="/#home" className="text-gray-700 hover:text-blue-600 font-medium block" onClick={(e) => scrollToSection('home', e)}>{t.home}</Link></li>
              <li><Link to="/#features" className="text-gray-700 hover:text-blue-600 font-medium block" onClick={(e) => scrollToSection('features', e)}>{t.features}</Link></li>
              <li><Link to="/#pricing" className="text-gray-700 hover:text-blue-600 font-medium block" onClick={(e) => scrollToSection('pricing', e)}>{t.pricing}</Link></li>
              <li><Link to="/about" className="text-gray-700 hover:text-blue-600 font-medium block" onClick={() => setMobileMenuOpen(false)}>{t.about}</Link></li>
              <li><Link to="/contact" className="text-gray-700 hover:text-blue-600 font-medium block" onClick={() => setMobileMenuOpen(false)}>{t.contact}</Link></li>
              <li className="pt-2 border-t border-gray-200">
                <a href="https://app.sanctionscreener.eu" className="text-gray-700 hover:text-blue-600 font-medium block">{t.login}</a>
              </li>
              <li className="pt-2">
                <button 
                  onClick={handleSignupClick}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200 inline-block"
                >
                  {t.signup}
                </button>
              </li>
              <li className="pt-2 border-t border-gray-200">
                <LanguageSwitcher />
              </li>
            </ul>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header; 